import { formatNameLastFirst } from '@strata/helper-functions';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { IntakeStatusDetails, StatusDetailsDisplayStrings } from 'services/constants';
import { canCancelIntakeCase } from 'services/helpers';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { type IntakeCase, useCancelIntakeCaseMutation } from 'store/api/intakeCaseApi';

interface CancelIntakeCaseDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<
        IntakeCase,
        'intakeCaseId' | 'memberFirstName' | 'memberLastName' | 'intakeStatus'
    >;
}

const CanceledIntakeCaseReasons = {
    CANCELED_BY_PRESCR: StatusDetailsDisplayStrings[IntakeStatusDetails.CANCELED_BY_PRESCR],
    DRUG_NOT_IN_SCOPE: StatusDetailsDisplayStrings[IntakeStatusDetails.DRUG_NOT_IN_SCOPE],
    NONFORMULARY: StatusDetailsDisplayStrings[IntakeStatusDetails.NONFORMULARY],
    COVERAGE_TERMINATED: StatusDetailsDisplayStrings[IntakeStatusDetails.COVERAGE_TERMINATED],
    DUPLICATE_CASE: StatusDetailsDisplayStrings[IntakeStatusDetails.DUPLICATE_CASE],
    REAUTHORIZATION: StatusDetailsDisplayStrings[IntakeStatusDetails.REAUTHORIZATION],
    REFERRAL_ASSIGNED: StatusDetailsDisplayStrings[IntakeStatusDetails.REFERRAL_ASSIGNED],
    REFERRAL_SERVICEABLE: StatusDetailsDisplayStrings[IntakeStatusDetails.REFERRAL_SERVICEABLE],
} as const;

const ReasonsToStatusDetails = {
    [CanceledIntakeCaseReasons.CANCELED_BY_PRESCR]: IntakeStatusDetails.CANCELED_BY_PRESCR,
    [CanceledIntakeCaseReasons.DRUG_NOT_IN_SCOPE]: IntakeStatusDetails.DRUG_NOT_IN_SCOPE,
    [CanceledIntakeCaseReasons.NONFORMULARY]: IntakeStatusDetails.NONFORMULARY,
    [CanceledIntakeCaseReasons.COVERAGE_TERMINATED]: IntakeStatusDetails.COVERAGE_TERMINATED,
    [CanceledIntakeCaseReasons.DUPLICATE_CASE]: IntakeStatusDetails.DUPLICATE_CASE,
    [CanceledIntakeCaseReasons.REAUTHORIZATION]: IntakeStatusDetails.REAUTHORIZATION,
    [CanceledIntakeCaseReasons.REFERRAL_ASSIGNED]: IntakeStatusDetails.REFERRAL_ASSIGNED,
    [CanceledIntakeCaseReasons.REFERRAL_SERVICEABLE]: IntakeStatusDetails.REFERRAL_SERVICEABLE,
} as const;

const ValidationSchema = Yup.object({
    reason: Yup.string().oneOf(Object.values(CanceledIntakeCaseReasons)).required(),
});

export const CancelIntakeCaseDialog = ({
    isOpen,
    onClose,
    intakeCase,
}: CancelIntakeCaseDialogProps) => {
    const navigate = useNavigate();
    const [cancelIntakeCase] = useCancelIntakeCaseMutation();

    const permissions = useAppSelector((state) => state.auth.permissions);

    return (
        <Formik
            initialValues={{} as Yup.InferType<typeof ValidationSchema>}
            enableReinitialize
            validationSchema={ValidationSchema}
            onSubmit={async (values, { resetForm }) => {
                await cancelIntakeCase({
                    intakeCaseId: intakeCase.intakeCaseId,
                    reason: ReasonsToStatusDetails[values.reason],
                }).unwrap();
                SuccessToast({ message: 'Intake case canceled successfully.' });
                resetForm();
                onClose();
                navigate(AppRoutes.activeIntakeCases);
            }}
        >
            {(formik) => {
                const userCanCancelIntakeCase = canCancelIntakeCase(intakeCase, permissions);
                return (
                    <DialogWithForm
                        isOpen={isOpen}
                        dialogTitle="Confirm Cancel Intake Case"
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.resetForm();
                        }}
                        isPrimaryButtonDisabled={
                            !(formik.dirty && formik.isValid) ||
                            formik.isSubmitting ||
                            !userCanCancelIntakeCase
                        }
                        primaryButtonLabel="Yes, cancel intake case"
                        secondaryButtonLabel="No, return to intake case details"
                        dialogContent={
                            <>
                                <div className="margin__bottom--large">
                                    Are you sure want to cancel the intake case for this patient?
                                </div>
                                <div className="margin__bottom--medium font-semi-bold--capitalize">
                                    {formatNameLastFirst(
                                        intakeCase.memberLastName,
                                        intakeCase.memberFirstName,
                                    )}
                                </div>
                                <SuggestSingleSelectFormik
                                    id="reason"
                                    label="Reason"
                                    placeholderText="Select canceled reason"
                                    required
                                    values={Object.values(CanceledIntakeCaseReasons)}
                                />
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};
